import { z } from 'zod';

/**
 * Embodied-carbon DTOs (SIL-189).
 *
 * Backs:
 *   GET /api/client/skus/:skuId/carbon-footprint  (customer)
 *   the `carbonFootprint` block on GET /api/client/dashboard/summary
 *
 * SIL-397: `opsSkuCarbonFootprintResponseSchema` below is defined but has no
 * live route — `GET /api/skus/:skuId/carbon-footprint` (ops) does not exist
 * in apps/api/src/routes/skus.ts today (confirmed by exhaustive grep across
 * apps/operations/src too: no embodied-carbon surface on the ops asset-detail
 * page, only a use-phase/operational panel). That doc comment previously
 * claimed the route existed; it was stale/aspirational. Fixed here, not
 * built here — building it is out of this ticket's scope.
 *
 * Source of truth is db/schema/665_sku_carbon_footprint.sql — append-only
 * snapshots; "latest" is resolved by v_sku_carbon_footprint_latest.
 *
 * The full uncertainty bundle (value + min/max bounds + completeness +
 * methodologyVersion + method) is exposed ONLY on the detail endpoints.
 * The listing detail payload (SIL-191) carries ONLY the thin scalar set in
 * listingCarbonClaimFieldsSchema below — never the bounds/raw bundle; a
 * tooltip that needs bounds fetches the SIL-189 detail endpoint lazily.
 */

/**
 * Completeness of a carbon figure:
 *   - 'complete' — computed with no defaulted inputs (defaulted_fields empty)
 *   - 'partial'  — computed, but one or more inputs fell back to defaults
 *   - 'pending_classification' — asset-grain only: the asset has no SKU, or
 *     its SKU has no carbon snapshot yet. Never appears on SKU detail
 *     responses (those return a null footprint instead).
 */
export const carbonCompletenessSchema = z.enum([
  'complete',
  'partial',
  'pending_classification',
]);
export type CarbonCompleteness = z.infer<typeof carbonCompletenessSchema>;

/**
 * Shared uncertainty fragment — every full-bundle carbon response embeds
 * exactly this shape. Bounds satisfy min <= value <= max (DB CHECK chain).
 */
export const carbonUncertaintySchema = z.object({
  valueKgco2eq: z.coerce.number(),
  minKgco2eq: z.coerce.number(),
  maxKgco2eq: z.coerce.number(),
  completeness: carbonCompletenessSchema,
  methodologyVersion: z.string(),
  method: z.string(),
});
export type CarbonUncertainty = z.infer<typeof carbonUncertaintySchema>;

/**
 * Derive completeness from a snapshot's defaulted_fields jsonb.
 * Empty object/array (or null) → 'complete'; anything recorded → 'partial'.
 * The 'pending_classification' case is a no-snapshot situation and is
 * decided by the caller, never by this function.
 */
export function deriveCarbonCompleteness(
  defaultedFields: unknown,
): Extract<CarbonCompleteness, 'complete' | 'partial'> {
  if (defaultedFields == null) return 'complete';
  if (Array.isArray(defaultedFields)) {
    return defaultedFields.length === 0 ? 'complete' : 'partial';
  }
  if (typeof defaultedFields === 'object') {
    return Object.keys(defaultedFields).length === 0 ? 'complete' : 'partial';
  }
  // Unexpected scalar payload — be honest and flag it as partial.
  return 'partial';
}

/**
 * GET /api/skus/:skuId/carbon-footprint and
 * GET /api/client/skus/:skuId/carbon-footprint response.
 * `carbonFootprint` is null when the SKU exists but has no snapshot yet
 * (the clean "uncomputed" shape); an unknown SKU is a plain 404.
 */
export const skuCarbonFootprintResponseSchema = z.object({
  skuId: z.string().uuid(),
  carbonFootprint: carbonUncertaintySchema
    .extend({ computedAt: z.string() })
    .nullable(),
});
export type SkuCarbonFootprintResponse = z.infer<
  typeof skuCarbonFootprintResponseSchema
>;

/**
 * One defaulted-field entry: which input fell back to a default, and why
 * (the free-text reason recorded by the compute pipeline, e.g. "archetype_default
 * (no cpu_model / processor-family text on OEM specs)"). Ops-only — the
 * client-facing skuCarbonFootprintResponseSchema deliberately omits this
 * (defaulted_fields never leaves the API on customer surfaces).
 */
export const carbonDefaultedFieldSchema = z.object({
  field: z.string(),
  reason: z.string(),
});
export type CarbonDefaultedField = z.infer<typeof carbonDefaultedFieldSchema>;

/**
 * Normalise a snapshot's defaulted_fields jsonb (Record<string, string> in
 * practice — see apps/ingestion/src/carbon/derive-input.ts) into an ordered
 * list of { field, reason }. Tolerates the array-of-keys and malformed-scalar
 * shapes defensively, same as deriveCarbonCompleteness/defaultedFieldKeys
 * above, but never throws — this feeds a UI list, not a security gate.
 */
export function defaultedFieldsList(defaultedFields: unknown): CarbonDefaultedField[] {
  if (defaultedFields == null) return [];
  if (Array.isArray(defaultedFields)) {
    return defaultedFields
      .filter((k): k is string => typeof k === 'string')
      .map((field) => ({ field, reason: '' }));
  }
  if (typeof defaultedFields === 'object') {
    return Object.entries(defaultedFields as Record<string, unknown>).map(([field, reason]) => ({
      field,
      reason: typeof reason === 'string' ? reason : JSON.stringify(reason),
    }));
  }
  return [];
}

/**
 * Shape for an ops-facing SKU carbon-footprint response, extended with the
 * ops-only defaultedFields list (making each figure's weakness visible
 * per-SKU). `carbonFootprint` is null when the SKU exists but has no
 * snapshot yet (the clean "uncomputed" shape); an unknown SKU is a plain
 * 404. SIL-397: NOT currently backed by a live route — see the module
 * header. Defined here so a future ops SKU-detail carbon surface has a
 * ready-made contract; do not treat its existence as evidence the route
 * exists.
 */
export const opsSkuCarbonFootprintResponseSchema = z.object({
  skuId: z.string().uuid(),
  carbonFootprint: carbonUncertaintySchema
    .extend({
      computedAt: z.string(),
      defaultedFields: z.array(carbonDefaultedFieldSchema),
    })
    .nullable(),
});
export type OpsSkuCarbonFootprintResponse = z.infer<
  typeof opsSkuCarbonFootprintResponseSchema
>;

/**
 * Embodied-carbon aggregate block on the client dashboard summary bundle.
 *
 * totalMin/totalMax are sums of the per-SKU bounds — stated as
 * sum-of-independent-bounds (no correlation between SKU uncertainties is
 * assumed). methodologyVersion is null when no asset has a computed
 * footprint. pendingClassificationCount counts assets with no SKU or whose
 * SKU has no snapshot yet.
 */
export const clientDashboardCarbonFootprintSchema = z.object({
  totalEmbodiedKgco2eq: z.coerce.number(),
  totalMin: z.coerce.number(),
  totalMax: z.coerce.number(),
  methodologyVersion: z.string().nullable(),
  computedAssetCount: z.number().int(),
  pendingClassificationCount: z.number().int(),
});
export type ClientDashboardCarbonFootprint = z.infer<
  typeof clientDashboardCarbonFootprintSchema
>;

// ---------------------------------------------------------------------------
// SIL-191 — listing-detail avoided-carbon claim
// ---------------------------------------------------------------------------

/**
 * Canonical plain-English explainer for what "embodied carbon" means. One
 * sentence, tooltip/caption weight — every surface that shows an embodied
 * carbon figure renders THIS constant (no per-surface rewording; copy drift
 * is a legal-review headache). Additive only: it never replaces or edits the
 * legal-approved claim copy or disclaimers, and like them must never sit
 * near the words "offset", "compensate", or "ESG-grade".
 */
export const CARBON_EMBODIED_EXPLAINER =
  "Embodied carbon is the CO2 emitted making this hardware - manufacturing, shipping, and end-of-life disposal - not from running it. Figures model a realistic production configuration, not the manufacturer's minimum test configuration, so they may exceed a vendor datasheet number.";

/**
 * Methodology page for the avoided-carbon claim (SIL-193, repointed by
 * SIL-352). The old value pointed at the internal Cloudflare Pages wiki,
 * which is behind Cloudflare Access (GitHub OAuth) and 404s/login-walls for
 * every customer. This now resolves to an in-app customer-portal route,
 * `apps/customer/src/routes/carbon-methodology.tsx`, hand-mirrored from
 * `wiki/09-product/carbon-methodology.md` (same pattern as `/privacy`).
 *
 * That route sits behind the customer portal's normal Cognito auth gate —
 * it is reachable to any logged-in customer-portal user, but NOT to an
 * unauthenticated visitor (e.g. a third-party auditor or prospective buyer
 * without a SiliconX account). That gap was a deliberate, informed
 * trade-off made on ticket SIL-352 (see its comments), not an oversight.
 */
export const CARBON_METHODOLOGY_URL =
  'https://portfolio.siliconx.tech/carbon-methodology';

/**
 * Thin scalar set embedded on GET /api/client/listings/:id (SIL-191),
 * following the fmvMedUsd embedded-scalar pattern. All three are null
 * together when the listing has no SKU, the SKU has no snapshot, or the
 * claim is suppressed by shouldShowCarbonClaim. Deliberately NO bounds —
 * the tooltip fetches the SIL-189 detail endpoint for those.
 */
export const listingCarbonClaimFieldsSchema = z.object({
  /** The snapshot's gwp_embodied_max — the honest "up to X" number. */
  carbonAvoidedUpToKgco2eq: z.coerce.number().nullable(),
  carbonMethodologyVersion: z.string().nullable(),
  carbonCompleteness: carbonCompletenessSchema.nullable(),
});
export type ListingCarbonClaimFields = z.infer<
  typeof listingCarbonClaimFieldsSchema
>;

/**
 * Model-identity keys of an embodied-carbon estimate (SIL-196). A
 * defaulted-fields key equal to one of these (case-insensitive) means the
 * estimate could not resolve WHAT the hardware is — the CPU model name or
 * the GPU archetype fell back to a generic default — and the claim must not
 * be shown.
 *
 * Deliberately NOT a cpu/ram/gpu prefix rule: ram/disk are ALWAYS
 * archetype-defaulted by design (only max-supported RAM is known; using it
 * would overstate ~10x), so a prefix gate on them suppresses every SKU
 * forever. That capacity uncertainty is already honest in the min-max
 * bounds; only unresolved model identity is disqualifying.
 */
export const CARBON_CLAIM_MODEL_IDENTITY_KEYS = [
  'cpu.name',
  'gpu.archetype',
] as const;

function defaultedFieldKeys(defaultedFields: unknown): string[] | null {
  if (defaultedFields == null) return [];
  if (Array.isArray(defaultedFields)) {
    return defaultedFields.filter((k): k is string => typeof k === 'string');
  }
  if (typeof defaultedFields === 'object') {
    return Object.keys(defaultedFields);
  }
  // Unexpected scalar payload — we cannot prove the defaults are non-core.
  return null;
}

/**
 * SIL-191/SIL-196 claim gate, shared so passport/CSV surfaces apply the
 * same rule:
 *   - 'complete'  → show.
 *   - 'partial'   → show UNLESS a model-identity key ('cpu.name' /
 *                   'gpu.archetype') was defaulted; a malformed
 *                   defaulted_fields payload suppresses (fail closed).
 *                   ram/disk/psu/case/units defaults do NOT suppress —
 *                   their uncertainty is carried in the min-max bounds.
 *   - anything else ('pending_classification', no snapshot) → suppress.
 */
export function shouldShowCarbonClaim(
  completeness: CarbonCompleteness,
  defaultedFields: unknown,
): boolean {
  if (completeness === 'complete') return true;
  if (completeness !== 'partial') return false;
  const keys = defaultedFieldKeys(defaultedFields);
  if (keys === null) return false;
  return !keys.some((key) =>
    CARBON_CLAIM_MODEL_IDENTITY_KEYS.some(
      (identityKey) => key.toLowerCase() === identityKey,
    ),
  );
}

/**
 * Legal-approved claim copy — verbatim, do not edit without legal sign-off.
 * X is rounded UP so the stated ceiling never understates the snapshot max
 * (an "up to" claim must bound the estimate from above). Never use
 * "offset", "compensate", or "ESG-grade" anywhere near this claim.
 *
 * Deliberately versionless: legal confirmed the methodology version is not
 * required on display surfaces — provenance lives on the methodology page,
 * in the snapshots, and in the CSV export's methodology_version column. The
 * stored raw version ("boaviztapi:docker-2.3.0;api-2.3.0") must never be
 * rendered verbatim in customer-facing copy.
 */
export function buildCarbonClaimCopy(upToKgco2eq: number): string {
  const x = Math.ceil(upToKgco2eq).toLocaleString('en-US');
  return `This unit's estimated embodied carbon footprint is up to ${x} kg CO2eq, computed via Boavizta's open-source methodology. Purchasing refurbished instead of new-manufactured equivalent hardware can avoid this embodied footprint.`;
}
